import { FormikProps, getIn } from "formik";
import dayjs from "dayjs";
import { startCase } from "lodash";
import {
  AUTHORIZATION_EXPIRY,
  CCM_CONSENT_INITIAL_VALUES,
  CONSENT_DATE_VALUE_FORMAT,
  GENDER,
} from "./ccm-consent-constant";
import {
  CONSENT_EXPIRY_TEXT,
  EXPIRY_NOTE_DATE_FORMAT,
  NON_DIGIT_REGEX,
  PHONE_DIGIT_COUNT,
} from "./ccm-consent-copy-constant";
import {
  AuthorizationExpiry,
  CcmConsentFormValues,
  ConsentFieldConfig,
  ConsentPrefillEmergencyContact,
  ConsentPrefillSource,
  Gender,
} from "./ccm-consent-types";

const isGender = (value?: string): value is Gender =>
  Object.values<string>(GENDER).includes(value ?? "");

export const toConsentDate = (date?: string | Date | null): string | null =>
  date && dayjs(date).isValid()
    ? dayjs(date).format(CONSENT_DATE_VALUE_FORMAT)
    : null;

export const toConsentGender = (sex?: string): Gender | "" => {
  const gender = sex?.toUpperCase();
  return isGender(gender) ? gender : "";
};

export const toConsentStreet = (source: ConsentPrefillSource): string =>
  [source.address?.line1, source.address?.line2]
    .filter((line) => line?.trim())
    .join(", ");

export const toConsentPhone = (source: ConsentPrefillSource): string =>
  (source.mobilePhone || source.mobileNumber || "")
    .replace(NON_DIGIT_REGEX, "")
    .slice(-PHONE_DIGIT_COUNT);

export const toConsentLabel = (value?: string | null): string =>
  value ? startCase(value.toLowerCase()) : "";

export const toConsentLanguage = (source: ConsentPrefillSource): string =>
  Object.values(source.languages ?? {})[0] ?? "";

export const firstEmergencyContact = (
  source: ConsentPrefillSource
): ConsentPrefillEmergencyContact | undefined =>
  Array.from(source.emergencyContacts ?? [])[0];

export const toContactName = (
  contact?: ConsentPrefillEmergencyContact
): string =>
  [contact?.firstName, contact?.lastName]
    .filter((name) => name?.trim())
    .join(" ");

/** "Date" means the form's default period from today. */
export const toDefaultExpiryDate = (defaultMonths: number): string =>
  dayjs().add(defaultMonths, "month").format(CONSENT_DATE_VALUE_FORMAT);

export interface ExpiryNote {
  text: string;
  // The expiry date, rendered emphasised after `text`.
  date?: string;
  after?: string;
}

/** The sentence under the expiry choice, once one is made. */
export const getExpiryNote = (
  expiry: AuthorizationExpiry | "",
  expiryDate: string | null,
  defaultMonths: number
): ExpiryNote | null => {
  if (expiry === AUTHORIZATION_EXPIRY.NEVER_EXPIRES)
    return { text: CONSENT_EXPIRY_TEXT.NEVER };
  if (expiry !== AUTHORIZATION_EXPIRY.DATE) return null;
  const date = expiryDate
    ? dayjs(expiryDate)
    : dayjs().add(defaultMonths, "month");
  return {
    text: CONSENT_EXPIRY_TEXT.PERIOD_BEFORE_DATE.replace(
      "{months}",
      String(defaultMonths)
    ),
    date: date.format(EXPIRY_NOTE_DATE_FORMAT),
    after: CONSENT_EXPIRY_TEXT.PERIOD_AFTER_DATE,
  };
};

export const lockConsentFields = (
  fields: ConsentFieldConfig[],
  lockedNames: readonly string[]
): ConsentFieldConfig[] =>
  fields.map((field) => ({
    ...field,
    isDisabled: lockedNames.includes(field.name),
  }));

export const buildCcmConsentPrefill = (
  source: ConsentPrefillSource
): CcmConsentFormValues => ({
  ...CCM_CONSENT_INITIAL_VALUES,
  patient: {
    ...CCM_CONSENT_INITIAL_VALUES.patient,
    firstName: source.firstName ?? "",
    lastName: source.lastName ?? "",
    dateOfBirth: toConsentDate(source.birthDate),
    gender: toConsentGender(source.sex),
    address: toConsentStreet(source),
    city: source.address?.city ?? "",
    state: source.address?.state ?? "",
    zipCode: source.address?.zipcode ?? "",
    cellphone: toConsentPhone(source),
    race: toConsentLabel(source.race),
    preferredLanguage: toConsentLanguage(source),
  },
  emergencyContact: {
    ...CCM_CONSENT_INITIAL_VALUES.emergencyContact,
    name: toContactName(firstEmergencyContact(source)),
    cell: firstEmergencyContact(source)?.mobile ?? "",
    relationshipToPatient: toConsentLabel(
      firstEmergencyContact(source)?.relationshipWithPatient
    ),
  },
});

export const getConsentSignatureError = <T>(
  formik: FormikProps<T>,
  name: string
): string | undefined =>
  formik.submitCount > 0 || getIn(formik.touched, name)
    ? getIn(formik.errors, name)
    : undefined;
